// Totals for ORDER line items. These are not cart line items: the store API returns `unitPrice` and
// `totalPrice` as plain columns and puts the sale price under `priceDefinition.listPrice` — there is
// no `price` object at all (see `OrderLineItem` in api-types/storeApiTypes.d.ts). Reusing the cart
// helpers here reads `price.unitPrice`/`price.listPrice`, which is always undefined, so every total
// silently comes out as 0.

export interface OrderListPriceLike {
    price?: number | null;
    discount?: number | null;
}

export interface OrderLineItemLike {
    type?: string | null;
    quantity?: number | null;
    unitPrice?: number | null;
    totalPrice?: number | null;
    priceDefinition?: { listPrice?: OrderListPriceLike | null } | null;
}

export function getOrderListPrice(item?: OrderLineItemLike | null): OrderListPriceLike | null {
    return item?.priceDefinition?.listPrice ?? null;
}

export function isOrderItemDiscounted(item?: OrderLineItemLike | null): boolean {
    return Boolean(getOrderListPrice(item)?.discount);
}

// Pre-discount total for one line. Falls back to the paid total so an item without a list price
// still contributes its own value instead of nothing.
export function getOrderItemOriginalTotal(item?: OrderLineItemLike | null): number {
    if (!item) return 0;

    const listPrice = getOrderListPrice(item);
    if (listPrice?.discount && typeof listPrice.price === 'number') {
        return listPrice.price * (item.quantity ?? 0);
    }

    return item.totalPrice ?? (item.unitPrice ?? 0) * (item.quantity ?? 0);
}

/** Subtotal before sale discounts — the figure the checkout cart shows above its "Korting" row. */
export function calculateOrderProductSubtotal(items?: OrderLineItemLike[] | null): number {
    if (!Array.isArray(items)) return 0;

    return items.reduce((total, item) => total + getOrderItemOriginalTotal(item), 0);
}

/** Total sale discount across the order, as a positive number. */
export function calculateOrderDiscount(items?: OrderLineItemLike[] | null): number {
    if (!Array.isArray(items)) return 0;

    return items.reduce((total, item) => {
        if (!isOrderItemDiscounted(item)) return total;
        return total + (getOrderItemOriginalTotal(item) - (item.totalPrice ?? 0));
    }, 0);
}

/**
 * Loyalty base: the summed paid price of every line that is NOT on sale. Mirrors `getNonSaleTotal`
 * in useLoyaltyProgram (which only understands cart-shaped items), including its per-item ceil.
 */
export function getOrderNonSaleTotal(items?: OrderLineItemLike[] | null): number {
    if (!Array.isArray(items)) return 0;

    return items.reduce((total, item) => {
        if (isOrderItemDiscounted(item)) return total;
        return total + Math.ceil(item?.totalPrice ?? 0);
    }, 0);
}
